import { DESIGN_H, DESIGN_W } from '../app/theme';
import { CARD_ASPECT } from '../ui/cardSprite';

export const HOKM_HAND_MAX_WIDTH = DESIGN_W - 32;
export const HOKM_SCORE_PILL_Y = 118;
export const HOKM_WON_PILE_Y = HOKM_SCORE_PILL_Y + 50;

const CARD_W_BASE = 101;
const OPP_CARD_W_BASE = 84;
const SUB_CARD_SCALE = 0.92;
const TRICK_CARD_SCALE = 0.92;
const WON_CARD_SCALE = 0.8;

const OPP_HAND_Y_MIN = 235;
const OPP_SUB_Y = 397;
const DECK_CY = 622;
const MY_HAND_Y = 1030;
const RAIL_REFRESH_Y = 560;
const RAIL_CHAT_Y = 650;

const SUB_STACK_OVERHANG = 10;
const CARD_GROUP_GAP = 12;

const clamp = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, v));

export interface HokmLayout {
  cardW: number;
  oppCardW: number;
  subW: number;
  trickW: number;
  wonW: number;
  oppHandY: number;
  oppSubY: number;
  deckCY: number;
  mySubY: number;
  myHandY: number;
  dropZone: { x: number; y: number; w: number; h: number };
  railRefreshY: number;
  railChatY: number;
}

/**
 * Computes the responsive Hokm card bands in portrait design coordinates. The local hand remains
 * prominent, the face-down opponent fan is smaller, and the dependent rows move to keep clear gaps.
 */
export function computeHokmLayout(viewportHeight: number): HokmLayout {
  const drop = Math.max(0, viewportHeight - DESIGN_H);
  const mid = Math.round(drop * 0.4);
  const responsiveScale = clamp(viewportHeight / DESIGN_H, 1, 1.25);
  const cardW = Math.round(CARD_W_BASE * responsiveScale);
  const oppCardW = Math.round(OPP_CARD_W_BASE * responsiveScale);
  const subW = Math.round(cardW * SUB_CARD_SCALE);
  const trickW = Math.round(cardW * TRICK_CARD_SCALE);
  const wonW = Math.round(subW * WON_CARD_SCALE);
  const myHandY = MY_HAND_Y + drop;

  // Captured cards are rotated exactly 90° and spread only horizontally, so half their portrait
  // width is the pile's vertical overhang. Keep the opponent hand clear of it.
  const wonPileBottom = HOKM_WON_PILE_Y + Math.ceil(wonW / 2);
  const oppHandY = Math.ceil(Math.max(OPP_HAND_Y_MIN, wonPileBottom + CARD_GROUP_GAP));

  // A 13-card fan's rotated edge cards reach about 1.77 card widths below its anchor.
  const oppHandBottom = oppHandY + Math.ceil(oppCardW * 1.77);
  const oppSubY = Math.max(OPP_SUB_Y + Math.round(mid * 0.5), oppHandBottom + CARD_GROUP_GAP);
  const oppSubBottom = oppSubY + subW * CARD_ASPECT + SUB_STACK_OVERHANG;

  const trickH = trickW * CARD_ASPECT;
  const deckCY = Math.max(DECK_CY + mid, Math.ceil(oppSubBottom + CARD_GROUP_GAP + trickH / 2));
  const trickBottom = deckCY + trickH / 2;

  // Retain the original bottom-hand relationship on tall screens, but prevent the center trick from
  // colliding with the local sub-packs at the compact 1280px baseline.
  const naturalMySubY = Math.round(myHandY - cardW * CARD_ASPECT - subW * CARD_ASPECT - 24);
  const mySubY = Math.max(naturalMySubY, Math.ceil(trickBottom + CARD_GROUP_GAP));

  return {
    cardW,
    oppCardW,
    subW,
    trickW,
    wonW,
    oppHandY,
    oppSubY,
    deckCY,
    mySubY,
    myHandY,
    dropZone: { x: DESIGN_W / 2 - 160, y: deckCY - 130, w: 320, h: 260 },
    railRefreshY: RAIL_REFRESH_Y + mid,
    railChatY: RAIL_CHAT_Y + mid,
  };
}
